// WHAT: Dashboard tab component displaying Parqet portfolio overview with positions, dividends, and recent transactions.
// WHY:  Provides comprehensive overview of investment portfolio including holdings, dividend summary, and transaction history for quick analysis.
// DOMAIN: Investments → Parqet
// DEPENDENCIES: React, @/components/ui, lucide-react, ../hooks, ../components
// RISKS:
//   - WARN: Multiple hooks are called simultaneously; errors in any hook will affect the entire dashboard.
//   - NOTE: Currency formatting uses 'de-DE' locale; may not match user preferences.
//   - PERF: Rendering large tables may impact performance; consider virtualization for many positions/transactions.

import { RefreshCw, TrendingUp, DollarSign, PieChart } from "lucide-react";
import React from "react";

import { Card, Button, Badge, Table, type TableColumn } from "@/components/ui";

import { SecurityLogo } from "../components";
import {
  usePortfolioPositions,
  useDividends,
  useRecentTransactions,
  type PortfolioPosition,
  type RecentTransaction,
} from "../hooks";

/**
 * WHAT:
 *   Dashboard tab component displaying Parqet portfolio overview.
 *
 * WHY:
 *   Provides users with comprehensive overview of their investment portfolio including
 *   current positions, dividend summary, and recent transaction history for quick analysis
 *   and decision-making.
 *
 * BEHAVIOR:
 *   - Fetches portfolio positions, dividends, and recent transactions using custom hooks
 *   - Displays loading state while data is being fetched
 *   - Shows error state if any data fetch fails
 *   - Renders portfolio positions table with security logos, types, quantities, and invested amounts
 *   - Displays dividend summary with total dividends and transaction count
 *   - Shows recent transactions table with type badges, dates, amounts, and securities
 *   - Provides refresh functionality to reload all data
 *   - Calculates and displays total holdings and total invested amount
 *
 * RISKS:
 *   - WARN: Multiple hooks are called simultaneously; errors in any hook will affect the entire dashboard.
 *   - NOTE: Currency formatting uses 'de-DE' locale; may not match user preferences.
 *   - PERF: Rendering large tables may impact performance; consider virtualization for many positions/transactions.
 *   - NOTE: Total calculations assume data structure; may fail with unexpected data.
 *
 * @returns {JSX.Element} Dashboard tab component
 *
 */
/**
 * WHAT:
 *   Formats numeric amount as currency string.
 *
 * WHY:
 *   Provides consistent currency formatting across the dashboard.
 *
 * @param {number} amount - Amount to format
 *
 * @param {string} [currency="EUR"] - Currency code
 *
 *
 * @returns {string} Formatted currency string
 *
 */
const formatCurrency = (amount: number, currency = "EUR") => {
  return new Intl.NumberFormat("de-DE", {
    currency: currency,
    style: "currency",
  }).format(amount);
};

/**
 * WHAT:
 *   Formats date string to German locale format.
 *
 * WHY:
 *   Provides consistent date formatting for transaction dates.
 *
 * @param {string} dateString - Date string to format
 *
 * @returns {string} Formatted date string
 *
 */
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("de-DE");
};

/**
 * WHAT:
 *   Returns badge color variant based on transaction type.
 *
 * WHY:
 *   Provides visual distinction between different transaction types (buy, sell, dividend).
 *
 * @param {string} type - Transaction type (case-insensitive)
 *
 * @returns {'default' | 'destructive' | 'info' | 'success'} Badge color variant
 *
 */
const getTransactionTypeColor = (type: string): "default" | "destructive" | "info" | "success" => {
  switch (type.toUpperCase()) {
    case "BUY": {
      return "success";
    }
    case "SELL": {
      return "destructive";
    }
    case "DIVIDEND": {
      return "info";
    }
    default: {
      return "default";
    }
  }
};

export const DashboardTab: React.FC = () => {
  const {
    error: positionsError,
    loading: positionsLoading,
    positions,
    refetch: refetchPositions,
  } = usePortfolioPositions();
  const {
    error: dividendsError,
    loading: dividendsLoading,
    refetch: refetchDividends,
    summary: dividendSummary,
    totalDividends,
    transactions: dividends,
  } = useDividends();
  const {
    error: transactionsError,
    loading: transactionsLoading,
    refetch: refetchTransactions,
    transactions: recentTransactions,
  } = useRecentTransactions(20);

  const loading = positionsLoading || dividendsLoading || transactionsLoading;
  const error = positionsError ?? dividendsError ?? transactionsError;

  /**
   * WHAT:
   *   Refreshes all dashboard data by refetching from all hooks.
   *
   * WHY:
   *   Allows users to manually update dashboard data without page reload.
   *
   * SIDE EFFECTS:
   *   - Calls refetchPositions to reload portfolio positions
   *   - Calls refetchDividends to reload dividend data
   *   - Calls refetchTransactions to reload recent transactions
   *
   * RISKS:
   *   - NOTE: All refetches happen simultaneously; may cause multiple API calls.
   *   - PERF: Refreshing all data may take time; consider loading indicators.
   */
  const handleRefresh = () => {
    void refetchPositions();
    void refetchDividends();
    void refetchTransactions();
  };

  // NOTE: Calculate totals from positions data
  const totalHoldings = positions.length;
  const totalInvested = positions.reduce((sum, pos) => sum + (pos.invested_amount || 0), 0);

  // Portfolio Positions Table Columns
  const portfolioColumns: TableColumn[] = [
    {
      key: "name",
      label: "Security",
      render: (_value, row: Record<string, unknown>, _index) => {
        const position = row as PortfolioPosition;
        return (
          <div className="flex items-center gap-3">
            <SecurityLogo isin={position.isin} name={position.name} size={40} />
            <div>
              <div className="font-medium text-white">{position.name}</div>
              <div className="text-sm text-slate-400">{position.isin}</div>
            </div>
          </div>
        );
      },
      sortable: true,
    },
    {
      key: "asset_type",
      label: "Type",
      render: (value) => (
        <Badge size="sm" style="subtle" variant="outline">
          {typeof value === "string" ? value.toUpperCase() : "N/A"}
        </Badge>
      ),
      sortable: true,
    },
    {
      align: "right",
      key: "total_quantity",
      label: "Quantity",
      render: (value) => (typeof value === "number" ? value.toFixed(4) : "0.0000"),
      sortable: true,
    },
    {
      align: "right",
      key: "invested_amount",
      label: "Invested",
      render: (value) => formatCurrency(typeof value === "number" ? value : 0),
      sortable: true,
    },
  ];

  // Recent Transactions Table Columns
  const transactionColumns: TableColumn[] = [
    {
      key: "type",
      label: "Type",
      render: (value) => (
        <Badge size="sm" style="subtle" variant={getTransactionTypeColor(value as string)}>
          {typeof value === "string" ? value.toUpperCase() : "N/A"}
        </Badge>
      ),
      sortable: true,
    },
    {
      key: "transaction_date",
      label: "Date",
      render: (value) => formatDate(typeof value === "string" ? value : ""),
      sortable: true,
    },
    {
      key: "broker",
      label: "Broker",
      render: (value) => (value ?? <span className="text-slate-500">N/A</span>) as React.ReactNode,
    },
    {
      align: "right",
      key: "amount",
      label: "Amount",
      render: (value, row: Record<string, unknown>) =>
        formatCurrency(
          typeof value === "number" ? value : 0,
          (row as RecentTransaction).currency || "EUR",
        ),
      sortable: true,
    },
  ];

  if (
    loading &&
    positions.length === 0 &&
    dividends.length === 0 &&
    recentTransactions.length === 0
  ) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-tracq-bright-blue" />
          <span className="text-slate-300">Loading dashboard data...</span>
        </div>
      </div>
    );
  }

  if (
    error &&
    positions.length === 0 &&
    dividends.length === 0 &&
    recentTransactions.length === 0
  ) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={handleRefresh} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-slate-900">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Investment Dashboard</h2>
        <Button
          className="bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
          disabled={loading}
          onClick={handleRefresh}
          variant="outline"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-slate-800 border-slate-700" variant="elevated">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-400">Total Holdings</p>
                <p className="text-2xl font-bold text-white">{totalHoldings}</p>
              </div>
              <div className="text-tracq-bright-blue">
                <PieChart className="w-8 h-8" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-slate-800 border-slate-700" variant="elevated">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-400">Total Invested</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(totalInvested)}</p>
              </div>
              <div className="text-blue-500">
                <TrendingUp className="w-8 h-8" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-slate-800 border-slate-700" variant="elevated">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-400">Total Dividends</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(totalDividends)}</p>
              </div>
              <div className="text-yellow-500">
                <DollarSign className="w-8 h-8" />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Portfolio Positions */}
      <Card className="bg-slate-800 border-slate-700" variant="elevated">
        <div className="p-6 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Portfolio Positions</h3>
          <p className="text-sm text-slate-400 mt-1">Active positions (shares &gt; 0)</p>
        </div>
        <div className="p-6">
          {(() => {
            if (positionsLoading) {
              return (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tracq-bright-blue" />
                </div>
              );
            }
            if (positionsError) {
              return <div className="text-center py-8 text-red-400">{positionsError}</div>;
            }
            if (positions.length === 0) {
              return (
                <div className="text-center py-8 text-slate-400">
                  No active portfolio positions found. Import some transactions to see data here.
                </div>
              );
            }
            return (
              <Table
                className="text-slate-300"
                columns={portfolioColumns}
                data={positions}
                hoverable
                sortable
                striped
              />
            );
          })()}
        </div>
      </Card>

      {/* Dividends Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dividend Summary */}
        <Card className="bg-slate-800 border-slate-700" variant="elevated">
          <div className="p-6 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">Dividend Summary</h3>
            <p className="text-sm text-slate-400 mt-1">By year and month</p>
          </div>
          <div className="p-6">
            {(() => {
              if (dividendsLoading) {
                return (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tracq-bright-blue" />
                  </div>
                );
              }
              if (dividendsError) {
                return <div className="text-center py-8 text-red-400">{dividendsError}</div>;
              }
              if (dividendSummary.length === 0) {
                return (
                  <div className="text-center py-8 text-slate-400">No dividend data available.</div>
                );
              }
              return (
                <div className="space-y-3">
                  {dividendSummary.slice(0, 12).map((item) => (
                    <div
                      className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg"
                      key={`${String(item.year)}-${String(item.month)}`}
                    >
                      <div>
                        <div className="text-white font-medium">
                          {new Date(item.year, item.month - 1).toLocaleDateString("de-DE", {
                            month: "long",
                            year: "numeric",
                          })}
                        </div>
                        <div className="text-sm text-slate-400">
                          {String(item.transaction_count)} transaction
                          {item.transaction_count === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-white font-bold">
                          {formatCurrency(item.total_dividends, item.currency)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </Card>

        {/* Recent Dividends */}
        <Card className="bg-slate-800 border-slate-700" variant="elevated">
          <div className="p-6 border-b border-slate-700">
            <h3 className="text-lg font-semibold text-white">Recent Dividends</h3>
            <p className="text-sm text-slate-400 mt-1">Last 10 dividend transactions</p>
          </div>
          <div className="p-6">
            {(() => {
              if (dividendsLoading) {
                return (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tracq-bright-blue" />
                  </div>
                );
              }
              if (dividendsError) {
                return <div className="text-center py-8 text-red-400">{dividendsError}</div>;
              }
              if (dividends.length === 0) {
                return (
                  <div className="text-center py-8 text-slate-400">
                    No dividend transactions found.
                  </div>
                );
              }
              return (
                <div className="space-y-3">
                  {dividends.slice(0, 10).map((tx) => (
                    <div
                      className="flex justify-between items-center p-3 border border-slate-700 rounded-lg hover:bg-slate-700/50"
                      key={tx.id}
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <Badge size="sm" style="subtle" variant="info">
                            DIVIDEND
                          </Badge>
                          {tx.broker ? (
                            <span className="text-sm text-slate-400">{tx.broker}</span>
                          ) : null}
                        </div>
                        <div className="text-sm text-slate-300">
                          {formatDate(tx.transaction_date)}
                        </div>
                        {tx.payment_date ? (
                          <div className="text-xs text-slate-500">
                            Payment: {formatDate(tx.payment_date)}
                          </div>
                        ) : null}
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-white">
                          {formatCurrency(tx.net_dividend, tx.currency)}
                        </div>
                        {Boolean(tx.shares) && (
                          <div className="text-xs text-slate-400">
                            {tx.shares.toFixed(4)} shares
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card className="bg-slate-800 border-slate-700" variant="elevated">
        <div className="p-6 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Recent Transactions</h3>
          <p className="text-sm text-slate-400 mt-1">Last 20 transactions</p>
        </div>
        <div className="p-6">
          {(() => {
            if (transactionsLoading) {
              return (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tracq-bright-blue" />
                </div>
              );
            }
            if (transactionsError) {
              return <div className="text-center py-8 text-red-400">{transactionsError}</div>;
            }
            if (recentTransactions.length === 0) {
              return (
                <div className="text-center py-8 text-slate-400">No recent transactions found.</div>
              );
            }
            return (
              <Table
                className="text-slate-300"
                columns={transactionColumns}
                data={recentTransactions}
                hoverable
                sortable
                striped
              />
            );
          })()}
        </div>
      </Card>
    </div>
  );
};
